// v10/src/components/NotificationManager.js
// CAMBIOS:
//   + AlertsPanel muestra observaciones de pilotos (tipo observation_reported)
//   + Soporte para tipos de Flow Mode: flow_anomaly_observation, flow_anomaly_squawk, flow_anomaly_squawk_aog
//   + Botón "Marcar todo leído" para limpiar observaciones
//   + Tiempo transcurrido (hace 5m / hace 2h) en cada observación
//   + FIX: triple ⚠️ eliminado — título limpio sin emoji duplicado
//   + FIX i18n: useTranslation hook agregado en MarkReadButton y ObservationCard
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, BellOff, X, AlertTriangle, Download, CheckCircle, Eye } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import api from '../services/apiService';
import { POLLING_INTERVALS } from '../config/realtime';

// ── Tipos de notificación que van al panel de observaciones ───────
// Cualquier tipo acá:
//   - se renderiza con ObservationCard (botón ojo individual para marcar leído)
//   - lo cubre "Limpiar todo" (handleMarkAllObsRead)
//   - sale del bloque "Vencimientos" → no recibe el badge "Próximo"/"Urgente"
//     derivado de PRIORITY (que es solo para vencimientos de habilitación/CMA/etc.)
const OBS_TYPES = new Set([
  'observation_reported',
  'squawk_status_changed',
  'squawk_reported',
  'flow_anomaly_observation',
  'flow_anomaly_squawk',
  'flow_anomaly_squawk_aog',
  'flight_annulled',
  'aircraft_aog',
  'aircraft_mel',
  // Sin este tipo en la lista, la notif de "Service hecho" cae en el bloque
  // "Vencimientos" y no aparece como observación (ver ObservationCard).
  'aircraft_service_done',
  'package_negative_balance',
  'slot_modified',
  // Sin este tipo acá, "Stock bajo mínimo" (backend OBS_TYPES en
  // notificationsController.js) cae en el bucket "Vencimientos" con el
  // render de daysLeft/expired en vez de ObservationCard — llega al front
  // pero mal categorizado.
  'low_stock',
]);

// ── Helpers Web Push ──────────────────────────────────────────────
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64  = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw     = window.atob(base64);
  return Uint8Array.from([...raw].map(c => c.charCodeAt(0)));
}

// ── Hook: PWA install prompt ──────────────────────────────────────
export const usePWAInstall = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstallable, setIsInstallable]   = useState(false);
  const [isInstalled, setIsInstalled]       = useState(false);

  useEffect(() => {
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true); return;
    }
    const handler = (e) => { e.preventDefault(); setDeferredPrompt(e); setIsInstallable(true); };
    window.addEventListener('beforeinstallprompt', handler);
    window.addEventListener('appinstalled', () => { setIsInstalled(true); setIsInstallable(false); setDeferredPrompt(null); });
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const install = async () => {
    if (!deferredPrompt) return false;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    setDeferredPrompt(null); setIsInstallable(false);
    return outcome === 'accepted';
  };

  return { isInstallable, isInstalled, install };
};

// ── Hook: Web Push subscription ──────────────────────────────────
export const usePushSubscription = () => {
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [pushLoading,    setPushLoading]    = useState(false);

  const isSupported = 'serviceWorker' in navigator && 'PushManager' in window;

  useEffect(() => {
    if (!isSupported) return;
    navigator.serviceWorker.ready
      .then(reg => reg.pushManager.getSubscription())
      .then(sub  => { if (sub) setPushSubscribed(true); })
      .catch(() => {});
  }, [isSupported]);

  const subscribe = useCallback(async () => {
    if (!isSupported) return false;
    setPushLoading(true);
    try {
      const { data } = await api.get('/api/push/vapid-public-key');
      if (!data.publicKey) throw new Error('VAPID key no disponible');
      const reg = await navigator.serviceWorker.ready;
      const subscription = await reg.pushManager.subscribe({
        userVisibleOnly:      true,
        applicationServerKey: urlBase64ToUint8Array(data.publicKey),
      });
      await api.post('/api/push/subscribe', subscription.toJSON());
      setPushSubscribed(true);
      return true;
    } catch (err) {
      console.error('[Push] Error al suscribirse:', err.message);
      return false;
    } finally {
      setPushLoading(false);
    }
  }, [isSupported]);

  const unsubscribe = useCallback(async () => {
    if (!isSupported) return;
    setPushLoading(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await api.delete('/api/push/unsubscribe', { data: { endpoint: sub.endpoint } });
        await sub.unsubscribe();
      }
      setPushSubscribed(false);
    } catch (err) {
      console.error('[Push] Error al desuscribirse:', err.message);
    } finally {
      setPushLoading(false);
    }
  }, [isSupported]);

  return { pushSubscribed, pushLoading, isSupported, subscribe, unsubscribe };
};

// ── Hook: Browser notifications + polling ────────────────────────
export const useNotifications = () => {
  const [permission, setPermission] = useState(('Notification' in window) ? Notification.permission : 'denied');
  const [alerts, setAlerts]         = useState([]);
  const [loading, setLoading]       = useState(false);
  // IDs de alertas para las que ya disparamos `new Notification(...)` en esta
  // sesión. Cada `new Notification` re-pide el icon (splash PNG) — si el polling
  // las re-crea cada 30s acumula cientos de requests del PNG. Dedupe por id.
  const notifiedIdsRef = useRef(new Set());

  const requestPermission = async () => {
    if (!('Notification' in window)) return false;
    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      return result === 'granted';
    } catch (err) {
      // En algunos browsers (Safari) requestPermission puede rechazar si no
      // se llama desde un user gesture. No propagamos para no dejar al
      // caller con estado inconsistente (ej: botón "Activando..." colgado).
      console.warn('[Notif] requestPermission falló:', err?.message || err);
      return false;
    }
  };

  const fetchAlerts = useCallback(async () => {
    try {
      setLoading(true);
      const res  = await api.get('/api/notifications');
      const data = res.data.data || [];
      setAlerts(data);
      if (permission === 'granted' && data.length > 0) {
        const seen = notifiedIdsRef.current;
        const currentIds = new Set(data.map(a => a.id));
        // Limpiar ids viejos que ya no están en el feed: si vuelven, las
        // renotificamos. Mientras sigan presentes, no re-disparamos.
        seen.forEach(id => { if (!currentIds.has(id)) seen.delete(id); });
        data.filter(a => a.priority === 'high' && !seen.has(a.id)).forEach(alert => {
          if (!('Notification' in window)) return;
          new Notification(alert.title, { body: alert.body, icon: '/condor360-splash-logo.png', badge: '/condor360-splash-logo.png', tag: alert.id });
          seen.add(alert.id);
        });
      }
      return data;
    } catch (err) {
      console.error('Error fetching notifications:', err);
      return [];
    } finally {
      setLoading(false);
    }
  }, [permission]);

  useEffect(() => {
    const handler = () => fetchAlerts();
    window.addEventListener('condor360:refresh', handler);
    return () => window.removeEventListener('condor360:refresh', handler);
  }, [fetchAlerts]);

  return { permission, alerts, loading, requestPermission, fetchAlerts, setAlerts };
};

// ── Priority styles ───────────────────────────────────────────────
const PRIORITY = {
  high:   { bg: 'rgba(239,68,68,0.1)',   border: 'rgba(239,68,68,0.3)',   text: '#ef4444', label: 'Urgente' },
  medium: { bg: 'rgba(245,158,11,0.1)',  border: 'rgba(245,158,11,0.3)',  text: '#f59e0b', label: 'Próximo' },
  low:    { bg: 'rgba(59,130,246,0.1)',  border: 'rgba(59,130,246,0.25)', text: '#3b82f6', label: 'Aviso'   },
};
const EXPIRED_STYLE = {
  bg: 'rgba(239,68,68,0.08)', border: 'rgba(239,68,68,0.45)',
  text: '#dc2626', label: 'VENCIDO',
  barColor: 'linear-gradient(180deg,#dc2626,#991b1b)',
};

// ── Helpers ───────────────────────────────────────────────────────
// Los emoji fuera del BMP (🔴🟡🟢🛡️📋📝🚨🏥🔧✈️🚫💸📅) son pares
// surrogate en UTF-16. Sin el flag `u`, la clase de caracteres [...] los
// descompone en unidades sueltas: como varios de estos emoji comparten el
// mismo surrogate alto, cualquier emoji con ese mismo alto pero NO listado
// (p.ej. 🔧, antes ausente acá) matcheaba solo la mitad y dejaba un
// surrogate bajo huérfano al inicio del título — se renderiza como
// carácter roto ("◆?"). El flag `u` fuerza a matchear por code point
// completo, no por unidad UTF-16.
const cleanTitle = (title = '') =>
  title
    .replace(/^[⚠️🔴🟡🟢✅🛡️📋📝⛔🚨🏥🔧✈️🚫💸📅\s]+/u, '')
    .replace(/\s+/g, ' ')
    .trim();

// Derivar label y colores según tipo de notificación de Flow Mode
const getFlowAnomalyStyle = (type) => {
  if (type === 'flow_anomaly_squawk_aog') return {
    borderColor: 'rgba(239,68,68,0.5)', barGradient: '#ef4444, #dc2626',
    iconBg: 'rgba(239,68,68,0.12)', iconColor: '#ef4444', label: '⛔ AOG — Flow Mode',
  };
  if (type === 'flow_anomaly_squawk') return {
    borderColor: 'rgba(245,158,11,0.45)', barGradient: '#f59e0b, #d97706',
    iconBg: 'rgba(245,158,11,0.12)', iconColor: '#d97706', label: '🔴 Squawk — Flow Mode',
  };
  // flow_anomaly_observation
  return {
    borderColor: 'rgba(99,102,241,0.4)', barGradient: '#818cf8, #6d28d9',
    iconBg: 'rgba(99,102,241,0.1)', iconColor: '#818cf8', label: '📝 Observación — Flow Mode',
  };
};

// ── PWA Install Banner ────────────────────────────────────────────
export const InstallBanner = ({ onInstall, onDismiss, darkMode }) => (
  <motion.div
    initial={{ y: -80, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: -80, opacity: 0 }}
    transition={{ type: 'spring', stiffness: 200, damping: 22 }}
    style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100, background: darkMode ? 'linear-gradient(135deg, rgba(17,24,39,0.98), rgba(30,27,75,0.98))' : 'linear-gradient(135deg, #1d4ed8, #6d28d9)', backdropFilter: 'blur(16px)', padding: '12px 20px', display: 'flex', alignItems: 'center', gap: '12px', boxShadow: '0 4px 24px rgba(0,0,0,0.3)' }}
  >
    <div style={{ fontSize: '28px', flexShrink: 0 }}>✈️</div>
    <div style={{ flex: 1 }}>
      <p style={{ margin: 0, fontWeight: 800, fontSize: '14px', color: 'white' }}>Instalá Condor360</p>
      <p style={{ margin: 0, fontSize: '12px', color: 'rgba(255,255,255,0.7)' }}>Accedé sin internet desde tu pantalla de inicio</p>
    </div>
    <motion.button onClick={onInstall} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
      style={{ padding: '8px 16px', background: 'white', color: '#1d4ed8', border: 'none', borderRadius: '12px', fontWeight: 800, fontSize: '13px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
      <Download style={{ width: '13px', height: '13px' }} /> Instalar
    </motion.button>
    <button onClick={onDismiss} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.6)', padding: '4px', flexShrink: 0 }}>
      <X style={{ width: '16px', height: '16px' }} />
    </button>
  </motion.div>
);

// ── Notification Bell Button ──────────────────────────────────────
export const NotificationBell = ({ alerts, onClick, darkMode }) => {
  const highCount = alerts.filter(a => a.priority === 'high').length;
  const total     = alerts.length;
  const hasObs    = alerts.some(a => OBS_TYPES.has(a.type));
  return (
    <motion.button onClick={onClick} whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}
      style={{ position: 'relative', border: 'none', cursor: 'pointer', width: '44px', height: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '14px', background: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.9)', boxShadow: darkMode ? '0 4px 16px rgba(0,0,0,0.4)' : '0 4px 16px rgba(0,0,0,0.12)', color: total > 0 ? (highCount > 0 ? '#ef4444' : '#f59e0b') : (darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(30,40,80,0.5)') }}>
      {hasObs ? (
        <motion.div animate={{ rotate: [0, -15, 15, -10, 10, 0] }} transition={{ duration: 0.6, repeat: Infinity, repeatDelay: 3 }}>
          <Bell style={{ width: '20px', height: '20px' }} />
        </motion.div>
      ) : (
        <Bell style={{ width: '20px', height: '20px' }} />
      )}
      {total > 0 && (
        <motion.span initial={{ scale: 0 }} animate={{ scale: 1 }}
          style={{ position: 'absolute', top: '4px', right: '4px', width: '16px', height: '16px', borderRadius: '50%', background: highCount > 0 ? '#ef4444' : '#f59e0b', color: 'white', fontSize: '9px', fontWeight: 900, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {total > 9 ? '9+' : total}
        </motion.span>
      )}
    </motion.button>
  );
};

// ── Mark Read Button ──────────────────────────────────────────────
const MarkReadButton = ({ onMarkRead, notifLogId, iconBg, iconColor }) => {
  const { t } = useTranslation();
  const [marked, setMarked] = React.useState(false);
  const handleClick = (e) => {
    e.stopPropagation();
    setMarked(true);
    setTimeout(() => onMarkRead(notifLogId), 800);
  };
  return (
    <motion.button
      onClick={handleClick}
      whileHover={!marked ? { scale: 1.02 } : {}}
      whileTap={!marked ? { scale: 0.97 } : {}}
      animate={marked ? { opacity: 0.6 } : { opacity: 1 }}
      style={{ marginTop: '4px', padding: '6px 14px', borderRadius: '8px', border: 'none', background: marked ? 'rgba(34,197,94,0.15)' : iconBg, color: marked ? '#22c55e' : iconColor, fontSize: '11px', fontWeight: 700, cursor: marked ? 'default' : 'pointer', display: 'flex', alignItems: 'center', gap: '5px', fontFamily: 'inherit', transition: 'all 0.3s' }}
    >
      <Eye style={{ width: '11px', height: '11px' }} />
      {marked ? t('marked_read',{ns:'common'}) : t('read_mark',{ns:'common'})}
    </motion.button>
  );
};

// ── Observation Card ──────────────────────────────────────────────
const ObservationCard = ({ alert, darkMode, onMarkRead }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = React.useState(false);
  const title = cleanTitle(alert.title);
  const body  = alert.body || '';

  // Determinar estilos según tipo
  const isFlowAnomaly = alert.type?.startsWith('flow_anomaly');
  // aircraft_service_done: notif de "service resuelto".
  //   - Por default: verde (éxito, avión vuelve a operar / service realizado).
  //   - Ámbar si el mensaje trae "sigue AOG": el service se hizo pero el avión
  //     sigue inhabilitado por otra causa — no es éxito completo, no confundir
  //     al piloto con un verde de "podés volar".
  const isServiceDone = alert.type === 'aircraft_service_done';
  const serviceStillAog = isServiceDone && body.toLowerCase().includes('sigue aog');
  let borderColor, barGradient, iconBg, iconColor, typeLabel;

  if (isFlowAnomaly) {
    const s = getFlowAnomalyStyle(alert.type);
    borderColor = s.borderColor; barGradient = s.barGradient;
    iconBg = s.iconBg; iconColor = s.iconColor; typeLabel = s.label;
  } else if (isServiceDone) {
    borderColor = serviceStillAog ? 'rgba(245,158,11,0.4)'  : 'rgba(34,197,94,0.4)';
    barGradient = serviceStillAog ? '#f59e0b, #d97706'      : '#22c55e, #16a34a';
    iconBg      = serviceStillAog ? 'rgba(245,158,11,0.12)' : 'rgba(34,197,94,0.12)';
    iconColor   = serviceStillAog ? '#d97706'               : '#22c55e';
    typeLabel   = null;
  } else {
    const isSquawkStatus = alert.type === 'squawk_status_changed';
    const isSquawk = isSquawkStatus || body.toLowerCase().includes('squawk') || body.toLowerCase().includes('aog');
    const isResolved = isSquawkStatus && (body.toLowerCase().includes('resuelto') || body.toLowerCase().includes('cerrado'));
    borderColor = isResolved ? 'rgba(34,197,94,0.4)' : isSquawk ? 'rgba(239,68,68,0.4)' : 'rgba(245,158,11,0.35)';
    barGradient = isResolved ? '#22c55e, #16a34a' : isSquawk ? '#ef4444, #dc2626' : '#f59e0b, #ef4444';
    iconBg      = isResolved ? 'rgba(34,197,94,0.12)' : isSquawk ? 'rgba(239,68,68,0.12)' : 'rgba(245,158,11,0.12)';
    iconColor   = isResolved ? '#22c55e' : isSquawk ? '#ef4444' : '#d97706';
    typeLabel   = null;
  }

  const IconComp = (isServiceDone && !serviceStillAog)
    || (alert.type === 'squawk_status_changed'
        && (body.toLowerCase().includes('resuelto') || body.toLowerCase().includes('cerrado')))
    ? CheckCircle
    : AlertTriangle;

  const handleEyeClick = () => {
    if (expanded) { onMarkRead(alert.notifLogId); }
    else { setExpanded(true); }
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }} animate={{ opacity: alert.is_read ? 0.45 : 1, x: 0 }}
      transition={{ duration: 0.3 }}
      style={{ borderRadius: '14px', padding: '12px 14px', background: darkMode ? 'rgba(255,255,255,0.03)' : 'white', border: `1px solid ${borderColor}`, position: 'relative', overflow: 'hidden', cursor: 'pointer', filter: alert.is_read ? 'grayscale(0.3)' : 'none' }}
      onClick={() => setExpanded(e => !e)}
    >
      <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '3px', background: `linear-gradient(180deg,${barGradient})`, borderRadius: '3px 0 0 3px' }} />
      <div style={{ paddingLeft: '10px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px', marginBottom: expanded ? '8px' : '5px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '7px', minWidth: 0, flexWrap: 'wrap' }}>
            <div style={{ width: '22px', height: '22px', borderRadius: '6px', background: iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <IconComp style={{ width: '12px', height: '12px', color: iconColor }} />
            </div>
            <span style={{ fontWeight: 700, fontSize: '12px', color: darkMode ? 'rgba(255,255,255,0.9)' : '#0f172a', lineHeight: 1.3 }}>
              {title || (isFlowAnomaly ? 'Flow Mode' : t('observation_reported',{ns:'admin'}))}
            </span>
            {typeLabel && (
              <span style={{ fontSize: '9px', fontWeight: 700, color: iconColor, background: iconBg, padding: '1px 6px', borderRadius: '10px', whiteSpace: 'nowrap' }}>
                {typeLabel}
              </span>
            )}
            {alert.is_read && (
              <span style={{ fontSize: '9px', fontWeight: 700, color: '#64748b', background: 'rgba(100,116,139,0.12)', padding: '1px 6px', borderRadius: '10px', whiteSpace: 'nowrap' }}>
                Leída
              </span>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
            {alert.timeAgo && (
              <span style={{ fontSize: '10px', fontWeight: 600, color: darkMode ? 'rgba(255,255,255,0.3)' : '#94a3b8', whiteSpace: 'nowrap' }}>{alert.timeAgo}</span>
            )}
            <motion.button
              onClick={e => { e.stopPropagation(); handleEyeClick(); }}
              whileHover={{ scale: 1.15 }} whileTap={{ scale: 0.9 }}
              title={expanded ? t('read_mark',{ns:'common'}) : 'Ver detalle'}
              style={{ background: expanded ? iconBg : 'none', border: 'none', cursor: 'pointer', padding: '4px', borderRadius: '6px', color: expanded ? iconColor : (darkMode ? 'rgba(255,255,255,0.3)' : '#94a3b8'), display: 'flex', transition: 'all 0.2s' }}
            >
              <Eye style={{ width: '13px', height: '13px' }} />
            </motion.button>
          </div>
        </div>

        <p style={{ margin: 0, fontSize: '11.5px', color: darkMode ? 'rgba(255,255,255,0.55)' : '#475569', lineHeight: 1.5, paddingLeft: '29px' }}>
          {body.replace(/^[⚠️🔴🟡🟢✅🔧📝⛔\s]+/u, '').trim()}
        </p>

        <AnimatePresence>
          {expanded && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.2 }} style={{ overflow: 'hidden' }}>
              <div style={{ marginTop: '10px', paddingLeft: '29px', borderTop: `1px solid ${borderColor}`, paddingTop: '10px' }}>
                {alert.pilotName && (
                  <p style={{ margin: '0 0 6px', fontSize: '11px', color: darkMode ? 'rgba(255,255,255,0.5)' : '#64748b' }}>
                    <span style={{ fontWeight: 700 }}>Piloto:</span> {alert.pilotName}
                  </p>
                )}
                {alert.photos && alert.photos.filter(u => typeof u === 'string' && u.startsWith('http')).length > 0 && (
                  <div style={{ marginBottom: '8px' }}>
                    <p style={{ margin: '0 0 6px', fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: darkMode ? 'rgba(255,255,255,0.35)' : '#94a3b8' }}>Fotos del defecto</p>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                      {alert.photos.filter(u => typeof u === 'string' && u.startsWith('http')).map((url, idx) => (
                        <a key={idx} href={url} target="_blank" rel="noopener noreferrer"
                          style={{ display: 'block', width: '56px', height: '56px', borderRadius: '8px', overflow: 'hidden', flexShrink: 0, border: `1px solid ${borderColor}` }}>
                          <img src={url} alt={`foto-${idx+1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        </a>
                      ))}
                    </div>
                  </div>
                )}
                {alert.resolution && (
                  <div style={{ marginBottom: '8px', padding: '7px 10px', borderRadius: '8px', background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.2)' }}>
                    <p style={{ margin: 0, fontSize: '11px', color: '#16a34a', fontStyle: 'italic' }}>✅ {alert.resolution}</p>
                  </div>
                )}
                {alert.items && alert.items.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', marginBottom: '8px' }}>
                    {alert.items.map((item, idx) => (
                      <div key={idx} style={{ padding: '7px 10px', borderRadius: '8px', background: darkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)', border: `1px solid ${borderColor}` }}>
                        <p style={{ margin: 0, fontSize: '11px', fontWeight: 700, color: darkMode ? 'rgba(255,255,255,0.8)' : '#374151' }}>{idx + 1}. {item.text}</p>
                        {item.notes && <p style={{ margin: '2px 0 0', fontSize: '10px', color: darkMode ? 'rgba(255,255,255,0.4)' : '#64748b', fontStyle: 'italic' }}>"{item.notes}"</p>}
                      </div>
                    ))}
                  </div>
                )}
                {(!alert.items || alert.items.length === 0) && alert.obsCount > 1 && (
                  <p style={{ margin: '0 0 8px', fontSize: '11px', color: darkMode ? 'rgba(255,255,255,0.4)' : '#64748b' }}>
                    <span style={{ fontWeight: 700 }}>Observaciones:</span> {alert.obsCount}
                  </p>
                )}
                <MarkReadButton onMarkRead={onMarkRead} notifLogId={alert.notifLogId} iconBg={iconBg} iconColor={iconColor} />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

// ── Alerts Panel ──────────────────────────────────────────────────
export const AlertsPanel = ({
  alerts, onClose, onRequestPermission, permission, darkMode,
  pushSubscribed, pushLoading, onPushSubscribe, onPushUnsubscribe, pushSupported,
  onMarkObsRead, onMarkAllObsRead,
}) => {
  // Red de seguridad: re-ordenar por sentAt descendente (más reciente
  // primero) dentro de cada bucket. El backend ya devuelve así, pero
  // esto blinda contra cualquier merge que rompa el orden desde otras
  // rutas / caches.
  const bySentAtDesc = (a, b) => {
    const da = a.sentAt ? new Date(a.sentAt).getTime() : 0;
    const db = b.sentAt ? new Date(b.sentAt).getTime() : 0;
    return db - da;
  };
  const obsAlerts   = alerts.filter(a => OBS_TYPES.has(a.type)).sort(bySentAtDesc);
  const otherAlerts = alerts.filter(a => !OBS_TYPES.has(a.type)).sort(bySentAtDesc);

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{ position: 'fixed', inset: 0, zIndex: 80, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}
    >
      <motion.div
        initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
        transition={{ type: 'spring', stiffness: 200, damping: 28 }}
        onClick={e => e.stopPropagation()}
        style={{ position: 'absolute', top: 0, right: 0, bottom: 0, width: '100%', maxWidth: '360px', background: darkMode ? '#111827' : 'white', boxShadow: '-8px 0 32px rgba(0,0,0,0.2)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
      >
        {/* Header */}
        <div style={{ padding: '20px', borderBottom: darkMode ? '1px solid rgba(255,255,255,0.07)' : '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: '12px', background: darkMode ? 'rgba(255,255,255,0.02)' : 'rgba(37,99,235,0.03)' }}>
          <div style={{ width: '38px', height: '38px', borderRadius: '12px', background: 'linear-gradient(135deg,#1d4ed8,#7c3aed)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Bell style={{ width: '18px', height: '18px', color: 'white' }} />
          </div>
          <div style={{ flex: 1 }}>
            <h2 style={{ margin: 0, fontSize: '16px', fontWeight: 800, color: darkMode ? 'white' : '#0f172a' }}>Alertas</h2>
            <p style={{ margin: 0, fontSize: '12px', color: darkMode ? 'rgba(255,255,255,0.4)' : '#94a3b8' }}>
              {alerts.length === 0 ? 'Sin alertas activas' : `${alerts.length} alerta${alerts.length !== 1 ? 's' : ''} activa${alerts.length !== 1 ? 's' : ''}`}
            </p>
          </div>
          <button onClick={onClose} style={{ background: darkMode ? 'rgba(255,255,255,0.08)' : '#f1f5f9', border: 'none', borderRadius: '10px', width: '32px', height: '32px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: darkMode ? 'rgba(255,255,255,0.5)' : '#64748b' }}>
            <X style={{ width: '15px', height: '15px' }} />
          </button>
        </div>

        {/* Web Push */}
        {pushSupported && (
          <div style={{ margin: '16px 16px 0', padding: '14px', borderRadius: '16px', background: pushSubscribed ? (darkMode ? 'rgba(34,197,94,0.08)' : 'rgba(34,197,94,0.06)') : (darkMode ? 'rgba(59,130,246,0.1)' : 'rgba(59,130,246,0.06)'), border: pushSubscribed ? '1px solid rgba(34,197,94,0.25)' : '1px solid rgba(59,130,246,0.2)' }}>
            {pushSubscribed ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <CheckCircle style={{ width: '18px', height: '18px', color: '#22c55e', flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <p style={{ margin: '0 0 2px', fontSize: '13px', fontWeight: 700, color: darkMode ? '#4ade80' : '#15803d' }}>Notificaciones push activas</p>
                  <p style={{ margin: 0, fontSize: '11px', color: darkMode ? 'rgba(255,255,255,0.4)' : '#64748b' }}>Recibirás alertas aunque la app esté cerrada</p>
                </div>
                <motion.button onClick={onPushUnsubscribe} disabled={pushLoading} whileTap={{ scale: 0.95 }}
                  style={{ padding: '6px 10px', background: 'none', border: darkMode ? '1px solid rgba(255,255,255,0.15)' : '1px solid #d1d5db', borderRadius: '8px', color: darkMode ? 'rgba(255,255,255,0.4)' : '#6b7280', fontSize: '11px', fontWeight: 600, cursor: 'pointer' }}>
                  <BellOff style={{ width: '13px', height: '13px' }} />
                </motion.button>
              </div>
            ) : (
              <>
                <p style={{ margin: '0 0 4px', fontSize: '13px', fontWeight: 700, color: darkMode ? '#93c5fd' : '#1d4ed8' }}>🔔 Notificaciones push</p>
                <p style={{ margin: '0 0 10px', fontSize: '12px', color: darkMode ? 'rgba(255,255,255,0.5)' : '#64748b' }}>Recibí alertas aunque la app esté cerrada</p>
                <motion.button
                  onClick={async () => {
                    // Defensive: garantizamos que cualquier error en el flujo
                    // de permission/subscribe NO deje al botón colgado en
                    // "Activando...". El estado pushLoading vive en el hook
                    // y se resetea en su finally interno; éste try/catch es
                    // belt-and-suspenders por si onRequestPermission lanza.
                    try {
                      if (permission !== 'granted') {
                        const granted = await onRequestPermission();
                        if (!granted) return;
                      }
                      await onPushSubscribe();
                    } catch (err) {
                      console.error('[Push] activation error:', err);
                    }
                  }}
                  disabled={pushLoading} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
                  style={{ width: '100%', padding: '10px', background: 'linear-gradient(135deg,#1d4ed8,#7c3aed)', color: 'white', border: 'none', borderRadius: '12px', fontWeight: 700, fontSize: '13px', cursor: pushLoading ? 'wait' : 'pointer', opacity: pushLoading ? 0.7 : 1, fontFamily: 'inherit' }}>
                  {pushLoading ? 'Activando...' : 'Activar notificaciones push'}
                </motion.button>
              </>
            )}
          </div>
        )}

        {/* Alerts list */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '12px 16px 20px' }}>
          {alerts.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px 0' }}>
              <CheckCircle style={{ width: '40px', height: '40px', margin: '0 auto 12px', color: '#22c55e', display: 'block' }} />
              <p style={{ fontWeight: 700, fontSize: '15px', color: darkMode ? 'rgba(255,255,255,0.6)' : '#374151', margin: '0 0 4px' }}>Todo en orden</p>
              <p style={{ fontSize: '13px', color: darkMode ? 'rgba(255,255,255,0.3)' : '#94a3b8', margin: 0 }}>No hay vencimientos próximos ni observaciones</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', paddingTop: '8px' }}>
              {obsAlerts.length > 0 && (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '2px' }}>
                    <span style={{ fontSize: '11px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: darkMode ? 'rgba(255,255,255,0.35)' : '#94a3b8' }}>
                      Novedades y observaciones
                    </span>
                    {obsAlerts.length > 1 && (
                      <motion.button onClick={onMarkAllObsRead} whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}
                        style={{ fontSize: '11px', fontWeight: 700, color: darkMode ? 'rgba(99,210,255,0.8)' : '#2563eb', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 6px', borderRadius: '6px' }}>
                        Limpiar todo
                      </motion.button>
                    )}
                  </div>
                  {obsAlerts.map(alert => (
                    <ObservationCard key={alert.id} alert={alert} darkMode={darkMode} onMarkRead={onMarkObsRead} />
                  ))}
                  {otherAlerts.length > 0 && <div style={{ height: '1px', background: darkMode ? 'rgba(255,255,255,0.07)' : '#f1f5f9', margin: '4px 0' }} />}
                </>
              )}
              {otherAlerts.length > 0 && (
                <>
                  {obsAlerts.length > 0 && (
                    <span style={{ fontSize: '11px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: darkMode ? 'rgba(255,255,255,0.35)' : '#94a3b8' }}>Vencimientos</span>
                  )}
                  {otherAlerts.map((alert, i) => {
                    const isExpired = alert.expired === true;
                    const style     = isExpired ? EXPIRED_STYLE : (PRIORITY[alert.priority] || PRIORITY.low);
                    const absDays   = Math.abs(alert.daysLeft);
                    return (
                      <motion.div key={alert.id} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.06 }}
                        style={{ borderRadius: '14px', padding: '12px 14px', background: style.bg, border: `1px solid ${style.border}`, position: 'relative', overflow: 'hidden' }}>
                        {isExpired && (
                          <motion.div animate={{ opacity: [1, 0.4, 1] }} transition={{ duration: 1.5, repeat: Infinity }}
                            style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '3px', background: style.barColor, borderRadius: '3px 0 0 3px' }} />
                        )}
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', paddingLeft: isExpired ? '8px' : '0' }}>
                          <AlertTriangle style={{ width: '15px', height: '15px', color: style.text, flexShrink: 0, marginTop: '2px' }} />
                          <div style={{ flex: 1 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '3px' }}>
                              <span style={{ fontWeight: 700, fontSize: '12px', color: darkMode ? 'white' : '#0f172a' }}>{cleanTitle(alert.title)}</span>
                              <span style={{ fontSize: '10px', fontWeight: 800, padding: '2px 7px', borderRadius: '99px', background: isExpired ? 'rgba(220,38,38,0.15)' : style.border, color: style.text, letterSpacing: '0.02em' }}>{style.label}</span>
                            </div>
                            <p style={{ margin: 0, fontSize: '11.5px', color: darkMode ? 'rgba(255,255,255,0.6)' : '#64748b', lineHeight: 1.4 }}>{alert.body}</p>
                          </div>
                          <div style={{ flexShrink: 0, textAlign: 'right', minWidth: '28px' }}>
                            {isExpired ? (
                              <span style={{ fontSize: '12px', fontWeight: 900, color: style.text, lineHeight: 1, display: 'block' }}>-{absDays}<span style={{ fontSize: '8px', display: 'block', fontWeight: 700 }}>días</span></span>
                            ) : alert.daysLeft === 0 ? (
                              <span style={{ fontSize: '20px', fontWeight: 900, color: style.text }}>!</span>
                            ) : (
                              <span style={{ fontSize: '18px', fontWeight: 900, color: style.text, lineHeight: 1 }}>
                                {alert.daysLeft}
                                <span style={{ fontSize: '9px', display: 'block', fontWeight: 600, color: darkMode ? 'rgba(255,255,255,0.4)' : '#94a3b8' }}>días</span>
                              </span>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </>
              )}
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

// ── Main Component ────────────────────────────────────────────────
const NotificationManager = ({ darkMode, isAuthenticated }) => {
  const { isInstallable, install }                                         = usePWAInstall();
  const { permission, alerts, fetchAlerts, requestPermission, setAlerts } = useNotifications();
  const { pushSubscribed, pushLoading, isSupported, subscribe, unsubscribe } = usePushSubscription();
  const [showBanner, setShowBanner]           = useState(false);
  const [showPanel,  setShowPanel]            = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(
    () => localStorage.getItem('condor360_install_dismissed') === 'true'
  );

  const getReadIds = () => {
    try { return new Set(JSON.parse(localStorage.getItem('condor360_read_notifs') || '[]')); } catch { return new Set(); }
  };
  const saveReadId = (id) => {
    try { const ids = getReadIds(); ids.add(id); localStorage.setItem('condor360_read_notifs', JSON.stringify([...ids].slice(-100))); } catch {}
  };
  const saveReadIds = (idsArray) => {
    try { const ids = getReadIds(); idsArray.forEach(id => ids.add(id)); localStorage.setItem('condor360_read_notifs', JSON.stringify([...ids].slice(-100))); } catch {}
  };

  useEffect(() => {
    if (isInstallable && !bannerDismissed) {
      const t = setTimeout(() => setShowBanner(true), 3000);
      return () => clearTimeout(t);
    }
  }, [isInstallable, bannerDismissed]);

  useEffect(() => {
    if (!isAuthenticated) return;
    const fetchAndFilter = async () => {
      await fetchAlerts();
      const readIds = getReadIds();
      if (readIds.size > 0) {
        setAlerts(prev => prev.filter(a => !a.notifLogId || !readIds.has(a.notifLogId)));
      }
    };
    fetchAndFilter();
    const interval = setInterval(fetchAndFilter, POLLING_INTERVALS.notifications); 
    return () => clearInterval(interval);
  }, [isAuthenticated, fetchAlerts]);

  useEffect(() => {
    if (!isAuthenticated || !isSupported || pushSubscribed) return;
    const autoSubscribe = async () => {
      try {
        if (!('Notification' in window)) return;
        if (Notification.permission === 'granted') { await subscribe(); return; }
        const alreadyAsked = localStorage.getItem('condor360_push_asked');
        if (!alreadyAsked && ('Notification' in window) && Notification.permission === 'default') {
          localStorage.setItem('condor360_push_asked', '1');
          if (!('Notification' in window)) return false;
    const result = ('Notification' in window) ? await Notification.requestPermission() : 'denied';
          if (result === 'granted') await subscribe();
        }
      } catch (_) {}
    };
    const t = setTimeout(autoSubscribe, 3000);
    return () => clearTimeout(t);
  }, [isAuthenticated, isSupported, pushSubscribed, subscribe]);

  const handleInstall = async () => {
    const accepted = await install();
    if (accepted) setShowBanner(false);
  };

  const handleDismissBanner = () => {
    setShowBanner(false); setBannerDismissed(true);
    localStorage.setItem('condor360_install_dismissed', 'true');
  };

  const handleMarkObsRead = async (notifLogId) => {
    if (!notifLogId) return;
    saveReadId(notifLogId);
    setAlerts(prev => prev.map(a => a.notifLogId === notifLogId ? { ...a, is_read: true } : a));
    setTimeout(() => setAlerts(prev => prev.filter(a => a.notifLogId !== notifLogId)), 800);
    try { await api.patch(`/api/notifications/${notifLogId}/read`); } catch (err) { console.error('Error marking as read:', err); }
  };

  const handleMarkAllObsRead = async () => {
    const obsIds = alerts.filter(a => OBS_TYPES.has(a.type) && a.notifLogId).map(a => a.notifLogId);
    if (!obsIds.length) return;
    saveReadIds(obsIds);
    setAlerts(prev => prev.map(a => OBS_TYPES.has(a.type) ? { ...a, is_read: true } : a));
    setTimeout(() => setAlerts(prev => prev.filter(a => !OBS_TYPES.has(a.type))), 800);
    await Promise.allSettled(obsIds.map(id => api.patch(`/api/notifications/${id}/read`)));
  };

  return (
    <>
      <AnimatePresence>
        {showBanner && !showPanel && (
          <InstallBanner onInstall={handleInstall} onDismiss={handleDismissBanner} darkMode={darkMode} />
        )}
      </AnimatePresence>

      <NotificationBell alerts={alerts} onClick={() => setShowPanel(true)} darkMode={darkMode} />

      <AnimatePresence>
        {showPanel && (
          <AlertsPanel
            alerts={alerts}
            onClose={() => setShowPanel(false)}
            onRequestPermission={requestPermission}
            permission={permission}
            darkMode={darkMode}
            pushSubscribed={pushSubscribed}
            pushLoading={pushLoading}
            onPushSubscribe={subscribe}
            onPushUnsubscribe={unsubscribe}
            pushSupported={isSupported}
            onMarkObsRead={handleMarkObsRead}
            onMarkAllObsRead={handleMarkAllObsRead}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default NotificationManager;
